import React, {
  createContext,
  useContext,
  useReducer,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import CartReducer, { CartAction, CartState } from "./CartReducer";
import { Product } from "data/data";
import { calculatingTotalPrice } from "utils/priceCalculations";

interface CartContextValue {
  Products: Product[];
  total_price: number;
  dispatch: React.Dispatch<CartAction>;
  searchProduct: string;
  setSearchProduct: React.Dispatch<React.SetStateAction<string>>;
  setProducts?: React.Dispatch<React.SetStateAction<Product[]>>;
  itemCount: number;
  subtotal: number;
  isCartDrawerOpen: boolean;
  openCartDrawer: () => void;
  closeCartDrawer: () => void;
}

const Cartcontext = createContext<CartContextValue>({
  Products: [],
  dispatch: () => undefined,
  total_price: 0,
  searchProduct: "",
  setSearchProduct: () => undefined,
  itemCount: 0,
  subtotal: 0,
  isCartDrawerOpen: false,
  openCartDrawer: () => undefined,
  closeCartDrawer: () => undefined,
});

// Cart is persisted in sessionStorage (not localStorage) so it clears when
// the browser tab closes — preventing yesterday's cart, or one merchant's
// items, from leaking into a fresh ordering session on the same domain.
const getInitialValue = (): CartState => {
  try {
    const initialState = sessionStorage.getItem("orderCards");
    if (!initialState) {
      return { Products: [], total_price: 0 };
    }
    return JSON.parse(initialState);
  } catch {
    return { Products: [], total_price: 0 };
  }
};

export const ProductCartContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(CartReducer, undefined, getInitialValue);
  const [searchProduct, setSearchProduct] = useState<string>("");
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);

  // Sync cart state to sessionStorage on every change
  useEffect(() => {
    sessionStorage.setItem("orderCards", JSON.stringify(state));
  }, [state]);

  // Computed values
  const itemCount = useMemo(
    () => state.Products.reduce((acc, item) => acc + (item.quantity || 1), 0),
    [state.Products]
  );

  const subtotal = useMemo(
    () =>
      state.Products.reduce(
        (acc, item) => acc + calculatingTotalPrice(item),
        0
      ),
    [state.Products]
  );

  const openCartDrawer = useCallback(() => setIsCartDrawerOpen(true), []);
  const closeCartDrawer = useCallback(() => setIsCartDrawerOpen(false), []);

  const value = useMemo(
    () => ({
      ...state,
      dispatch,
      searchProduct,
      setSearchProduct,
      itemCount,
      subtotal,
      isCartDrawerOpen,
      openCartDrawer,
      closeCartDrawer,
    }),
    [state, searchProduct, itemCount, subtotal, isCartDrawerOpen, openCartDrawer, closeCartDrawer]
  );

  return <Cartcontext.Provider value={value}>{children}</Cartcontext.Provider>;
};

export const useCartDataProvider = () => {
  return useContext(Cartcontext);
};
